import type { ProductPhi, ProductPhiType } from '../services/sprayService';

/**
 * Translation function signature compatible with both react-i18next's TFunction
 * and a thin test stub. Kept local to avoid dragging the react-i18next type
 * generics through consumers of this module.
 */
export type TranslateFn = (key: string, opts?: Record<string, unknown>) => string;

const EMPTY_PHI_DISPLAY = '—';

const PHI_TYPE_KEYS: Record<ProductPhiType, string> = {
  days: 'spray.products.phiDays',
  petal_drop_80: 'spray.products.phiPetalDrop80',
  month_of_year: 'spray.products.phiMonthOfYear',
};

/**
 * Render a PHI sub-document for display. Each type is formatted to match the
 * way it is authored in the product form:
 *
 *   - `days`          "{value} days"            (delegates to phiDaysFormat)
 *   - `petal_drop_80` "80% Petal Drop" label    (ignores value — type is self-describing)
 *   - `month_of_year` returns value verbatim    (value is already human-readable)
 *
 * Absent / blank PHI renders as an em-dash.
 */
export function formatPhi(phi: ProductPhi | undefined, t: TranslateFn): string {
  if (!phi || !phi.value || !phi.value.trim()) return EMPTY_PHI_DISPLAY;
  switch (phi.type) {
    case 'days':
      return t('spray.products.phiDaysFormat', { value: phi.value });
    case 'petal_drop_80':
      return t('spray.products.phiPetalDrop80');
    case 'month_of_year':
      return phi.value;
    default:
      return phi.value;
  }
}

/**
 * Translate a bare PHI type into its human label. Used by the form's PHI
 * type selector and by tooltips/aria labels.
 */
export function phiTypeLabel(type: ProductPhiType, t: TranslateFn): string {
  const key = PHI_TYPE_KEYS[type];
  if (!key) return type;
  return t(key);
}
